import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  CheckCircle2, XCircle, Edit3, Download, Upload, FileText, Trash2,
  Building2, Calendar, Paperclip, Image as ImageIcon, Loader2, ExternalLink,
  CreditCard, Wallet, BadgeCheck,
} from "lucide-react";
import { toast, Toaster } from "sonner";
import { fmtEUR } from "@/lib/format";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmtFecha = (iso) => iso ? new Date(iso).toLocaleDateString("es-ES") : "";

function superficieM2(it) {
  if (it.base_cm && it.altura_cm) {
    return (parseFloat(it.base_cm) / 100) * (parseFloat(it.altura_cm) / 100);
  }
  return null;
}

function calcItem(it) {
  const precio = parseFloat(it.precio_unitario) || 0;
  const desc = parseFloat(it.descuento_pct) || 0;
  const qty = parseFloat(it.cantidad) || 0;
  const unidad = (it.unidad || "").toLowerCase();
  let qtyEff = qty;
  if (unidad === "m2") {
    const m2 = superficieM2(it);
    if (m2) qtyEff = m2 * qty;
  } else if (unidad === "ml" && it.base_cm) {
    qtyEff = (parseFloat(it.base_cm) / 100) * qty;
  }
  return qtyEff * precio * (1 - desc / 100);
}

export default function PortalPresupuesto() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [openDecision, setOpenDecision] = useState(null); // 'aceptado' | 'pedir_cambios' | 'rechazado'
  const [comentario, setComentario] = useState("");
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  // Selección de items (sólo aplica si hay opcionales/grupos)
  const [seleccion, setSeleccion] = useState({}); // {itemId: true/false}
  // Chat
  const [chatMsg, setChatMsg] = useState("");
  const [sendingChat, setSendingChat] = useState(false);
  const chatBottomRef = useRef(null);
  // Progreso (Kanban) — sólo si el presupuesto ya se convirtió en petición
  const [progreso, setProgreso] = useState(null);
  // Pagos
  const [pagos, setPagos] = useState([]);
  const [iniciandoPago, setIniciandoPago] = useState(false);
  // Factura
  const [factura, setFactura] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/portal-pres/${token}`);
      setData(data);
      // Inicializar selección: si ya hay items_seleccionados, usarla. Si no, los obligatorios + default
      const initSel = {};
      const yaSeleccionados = data.items_seleccionados;
      (data.items || []).forEach((it) => {
        if (yaSeleccionados) {
          initSel[it.id] = yaSeleccionados.includes(it.id);
        } else if (it.grupo) {
          initSel[it.id] = !!it.seleccionado_default;
        } else if (it.opcional) {
          initSel[it.id] = false;
        } else {
          initSel[it.id] = true;
        }
      });
      // Si hay grupo sin ningún default, marcar el primero
      const grupos = {};
      (data.items || []).forEach((it) => {
        if (it.grupo) {
          if (!grupos[it.grupo]) grupos[it.grupo] = [];
          grupos[it.grupo].push(it);
        }
      });
      Object.entries(grupos).forEach(([g, items]) => {
        if (!items.some((it) => initSel[it.id])) {
          initSel[items[0].id] = true;
        }
      });
      setSeleccion(initSel);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Enlace no válido o caducado");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  // Cargar pagos cuando el presupuesto está aceptado
  const loadPagos = async () => {
    try {
      const { data: dp } = await axios.get(`${API}/portal-pres/${token}/pagos`);
      setPagos(dp.pagos || []);
    } catch (_) { /* silent */ }
  };
  const loadFactura = async () => {
    try {
      const { data: dfi } = await axios.get(`${API}/portal-pres/${token}/factura-info`);
      setFactura(dfi?.factura || null);
    } catch (_) { /* silent */ }
  };
  useEffect(() => {
    if (data?.estado === "aceptado") {
      loadPagos();
      loadFactura();
    }
    // Detectar redirección desde pasarela (?pago=ok|ko)
    const url = new URL(window.location.href);
    const pago = url.searchParams.get("pago");
    if (pago === "ok") {
      toast.success("¡Pago recibido! Gracias 🎉");
      window.history.replaceState({}, "", url.pathname);
    } else if (pago === "ko") {
      toast.error("El pago no se completó. Inténtalo de nuevo.");
      window.history.replaceState({}, "", url.pathname);
    }
    // eslint-disable-next-line
  }, [data?.estado]);

  const iniciarPago = async () => {
    setIniciandoPago(true);
    try {
      // El backend usa SIEMPRE el cobro_habilitado del presupuesto.
      // No mandamos tipo ni importe: el cliente sólo paga lo que DNF ha habilitado.
      const { data: r } = await axios.post(`${API}/portal-pres/${token}/pagar`, {});
      if (r.pasarela === "mock") {
        // Abrir página mock en nueva pestaña
        window.open(r.url, "_blank");
        toast.info("Pasarela MOCK abierta. Completa o cancela el pago.");
        // Refrescar pagos cada 3s durante 30s
        let n = 0;
        const interval = setInterval(async () => {
          n += 1;
          await loadPagos();
          await load();
          if (n > 10) clearInterval(interval);
        }, 3000);
      } else if (r.pasarela === "redsys") {
        // Auto-submit form a Redsys
        const form = document.createElement("form");
        form.method = "POST";
        form.action = r.url;
        Object.entries(r.fields).forEach(([k, v]) => {
          const inp = document.createElement("input");
          inp.type = "hidden"; inp.name = k; inp.value = v;
          form.appendChild(inp);
        });
        document.body.appendChild(form);
        form.submit();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo iniciar el pago");
    } finally {
      setIniciandoPago(false);
    }
  };

  // Cargar progreso cuando hay petición + auto-refresh cada 30s
  useEffect(() => {
    let mounted = true;
    const fetchProg = async () => {
      try {
        const { data } = await axios.get(`${API}/portal-pres/${token}/progreso`);
        if (mounted) setProgreso(data);
      } catch (_) { /* silencioso */ }
    };
    fetchProg();
    const interval = setInterval(fetchProg, 30000);
    return () => { mounted = false; clearInterval(interval); };
  }, [token]);

  const enviarDecision = async () => {
    if (!openDecision) return;
    setBusy(true);
    try {
      const hayOpcionales = (data.items || []).some((it) => it.opcional || it.grupo);
      const itemsSel = hayOpcionales
        ? Object.entries(seleccion).filter(([_id, v]) => v).map(([id]) => id)
        : null;
      await axios.post(`${API}/portal-pres/${token}/decision`, {
        decision: openDecision,
        comentario,
        items_seleccionados: itemsSel,
      });
      toast.success(
        openDecision === "aceptado" ? "¡Genial! Hemos recibido tu confirmación 🎉" :
        openDecision === "pedir_cambios" ? "Hemos recibido tus comentarios" :
        "Hemos registrado tu rechazo"
      );
      const justAccepted = openDecision === "aceptado";
      setOpenDecision(null);
      setComentario("");
      // Refresh inmediato del estado del portal
      await load();
      // Si acaba de aceptar → scroll suave a la sección de pago
      if (justAccepted) {
        setTimeout(() => {
          const el = document.querySelector('[data-testid="portal-pagos"]');
          if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 600);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo enviar tu decisión");
    } finally { setBusy(false); }
  };

  const enviarChat = async () => {
    const txt = chatMsg.trim();
    if (!txt) return;
    setSendingChat(true);
    try {
      const { data: nuevoMsg } = await axios.post(`${API}/portal-pres/${token}/mensajes`, { texto: txt });
      // Actualización OPTIMISTA del state local: añadimos el mensaje al instante
      setData((prev) => prev ? { ...prev, mensajes: [...(prev.mensajes || []), nuevoMsg] } : prev);
      setChatMsg("");
      setTimeout(() => chatBottomRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
      // Y un load() en background por si acaso (no bloqueante)
      load();
    } catch (e) {
      toast.error("No se pudo enviar el mensaje");
    } finally { setSendingChat(false); }
  };

  const subirArchivo = async (file) => {
    if (!file) return;
    if (file.size > 20_000_000) { toast.error("Máximo 20 MB por archivo"); return; }
    setUploading(true);
    try {
      // Firma de Cloudinary (público con token)
      const { data: sig } = await axios.get(`${API}/portal-pres/${token}/cloudinary-signature`);
      const form = new FormData();
      form.append("file", file);
      form.append("api_key", sig.api_key);
      form.append("timestamp", sig.timestamp);
      form.append("signature", sig.signature);
      form.append("folder", sig.folder);
      const up = await fetch(`https://api.cloudinary.com/v1_1/${sig.cloud_name}/auto/upload`, {
        method: "POST", body: form,
      });
      // Lectura defensiva del body (esquiva "body stream already read" de extensiones del navegador)
      let raw = "";
      try { raw = await up.text(); } catch (_) { raw = ""; }
      if (!up.ok) {
        let detail = raw;
        try { detail = JSON.parse(raw)?.error?.message || raw; } catch (_) {}
        throw new Error(`Cloudinary ${up.status}: ${detail}`);
      }
      let cld;
      try { cld = JSON.parse(raw); }
      catch (_) { throw new Error("Cloudinary devolvió respuesta inválida"); }
      // Guardar en backend
      const { data: arch } = await axios.post(`${API}/portal-pres/${token}/archivos`, {
        nombre: file.name,
        url: cld.secure_url,
        tipo: file.type || cld.resource_type,
        tamano: file.size,
        public_id: cld.public_id,
      });
      // Actualización optimista
      setData((prev) => prev ? { ...prev, archivos_cliente: [...(prev.archivos_cliente || []), arch] } : prev);
      toast.success("Archivo enviado a DNF");
    } catch (e) {
      toast.error("Error subiendo archivo: " + (e.message || e));
      console.error("[portal subirArchivo]", e);
    } finally { setUploading(false); }
  };

  // Helpers para selección de items
  const toggleItem = (item) => {
    if (item.grupo) {
      // Radio: marcar éste, desmarcar resto del grupo
      const nuevo = { ...seleccion };
      (data.items || []).forEach((it) => {
        if (it.grupo === item.grupo) nuevo[it.id] = it.id === item.id;
      });
      setSeleccion(nuevo);
    } else if (item.opcional) {
      setSeleccion({ ...seleccion, [item.id]: !seleccion[item.id] });
    }
    // obligatorios no se pueden tocar
  };

  // Total recalculado en vivo con la selección
  const totalSeleccion = useMemo(() => {
    let subtotal = 0;
    (data?.items || []).forEach((it) => {
      const incluido = it.grupo || it.opcional ? seleccion[it.id] : true;
      if (incluido) subtotal += calcItem(it);
    });
    const iva = subtotal * (data?.iva_pct || 21) / 100;
    return { subtotal, iva, total: subtotal + iva };
  }, [data, seleccion]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-zinc-50 to-amber-50/40 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  if (err || !data) {
    return (
      <div className="min-h-screen bg-zinc-50 flex flex-col items-center justify-center p-8">
        <div className="text-2xl font-bold text-zinc-800 mb-2">Enlace no disponible</div>
        <div className="text-zinc-600 max-w-md text-center">{err || "Este enlace ya no es válido. Contacta con DNF para que te envíe uno nuevo."}</div>
      </div>
    );
  }

  const totales = data.totales || { subtotal: 0, iva: 0, total: 0 };
  const empresa = data.empresa || {};
  const snap = data.cliente_snapshot || {};
  const decisionActual = data.cliente_decision;
  const aceptado = data.estado === "aceptado";
  const rechazado = data.estado === "rechazado";

  return (
    <div className="min-h-screen bg-gradient-to-br from-zinc-50 to-amber-50/30">
      <Toaster richColors position="top-center" />

      {/* Banner superior con estado */}
      {aceptado && (
        <div className="bg-emerald-500 text-white py-3 text-center font-semibold text-sm shadow-md">
          <CheckCircle2 className="w-4 h-4 inline mr-2" />
          Presupuesto ACEPTADO el {fmtFecha(data.cliente_decision_at)}
          {(data.estado_pago !== "pagado") && (
            <span className="block text-xs font-normal text-emerald-50/90 mt-0.5">
              👇 Abona el anticipo (50%) o el total para que arranquemos producción
            </span>
          )}
        </div>
      )}
      {rechazado && (
        <div className="bg-red-500 text-white py-3 text-center font-semibold text-sm">
          Presupuesto rechazado el {fmtFecha(data.cliente_decision_at)}
        </div>
      )}
      {decisionActual === "pedir_cambios" && !aceptado && !rechazado && (
        <div className="bg-amber-500 text-white py-3 text-center font-semibold text-sm">
          <Edit3 className="w-4 h-4 inline mr-2" />
          Estamos revisando tus comentarios. Te enviaremos una nueva versión pronto.
        </div>
      )}

      <div className="max-w-4xl mx-auto p-4 md:p-8">

        {/* Header DNF */}
        <header className="bg-white rounded-2xl shadow-sm border border-zinc-200 p-6 mb-6">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <img src="/brand/logo-rotulaciondnf-light.png" alt="RotulaciónDNF" className="w-16 h-16 object-contain" />
              <div>
                <div className="text-xl font-bold text-zinc-900">{empresa.marca_comercial || "RotulaciónDNF"}</div>
                <div className="text-xs text-zinc-500">{empresa.razon_social} · CIF {empresa.cif}</div>
                <div className="text-xs text-zinc-500">{empresa.telefono} · {empresa.email}</div>
              </div>
            </div>
            <div className="text-right">
              <div className="text-amber-600 text-3xl font-bold leading-none" data-testid="numero-presupuesto">
                {data.numero}
              </div>
              <div className="text-xs text-zinc-500 mt-1">Fecha: <b>{fmtFecha(data.fecha)}</b></div>
              <div className="text-xs text-zinc-500">Válido hasta: <b>{fmtFecha(data.fecha_validez)}</b></div>
            </div>
          </div>
        </header>

        {/* Cliente + Total */}
        <div className="grid md:grid-cols-3 gap-4 mb-6">
          <div className="md:col-span-2 bg-white rounded-2xl shadow-sm border border-zinc-200 p-5">
            <div className="text-[11px] uppercase tracking-wider text-zinc-500 font-semibold mb-2 flex items-center gap-1.5">
              <Building2 className="w-3.5 h-3.5" /> Para
            </div>
            <div className="text-lg font-bold text-zinc-900">{snap.razon_social || "—"}</div>
            {snap.contacto_nombre && <div className="text-sm text-zinc-700">Atn.: {snap.contacto_nombre}</div>}
            {snap.cif && <div className="text-sm text-zinc-600">CIF: {snap.cif}</div>}
            {snap.direccion && <div className="text-sm text-zinc-600">{snap.direccion}</div>}
          </div>
          <div className="bg-gradient-to-br from-amber-400 to-amber-500 rounded-2xl shadow-md p-5 text-white">
            <div className="text-[11px] uppercase tracking-wider text-amber-50/90 font-semibold mb-1">Importe total</div>
            <div className="text-3xl font-bold" data-testid="total-presupuesto">{fmtEUR(totales.total)}</div>
            <div className="text-xs text-amber-50/80 mt-1">IVA ({data.iva_pct}%) incluido</div>
          </div>
        </div>

        {/* KANBAN — progreso del pedido (sólo si el presupuesto se convirtió en petición) */}
        {progreso?.convertido && (
          <KanbanProgreso progreso={progreso} />
        )}

        {/* PAGOS — sólo si el presupuesto está aceptado */}
        {aceptado && (
          <PagosCliente
            token={token}
            totales={totales}
            importePagado={data.importe_pagado || 0}
            estadoPago={data.estado_pago}
            pagos={pagos}
            cobroHabilitado={data.cobro_habilitado}
            iniciar={iniciarPago}
            busy={iniciandoPago}
          />
        )}

        {/* FACTURA — sólo si se ha emitido */}
        {aceptado && factura && (
          <div className="bg-white rounded-2xl shadow-sm border border-zinc-200 p-5 mb-6" data-testid="portal-factura">
            <div className="flex items-center gap-3 flex-wrap">
              <FileText className="w-5 h-5 text-amber-600" />
              <div className="flex-1 min-w-0">
                <div className="text-base font-semibold text-zinc-900">
                  Factura {factura.numero}
                  <span className={`ml-2 text-[10px] uppercase font-semibold px-2 py-0.5 rounded ${
                    factura.estado === "cobrada" ? "bg-emerald-50 text-emerald-700 border border-emerald-200" :
                    "bg-amber-50 text-amber-700 border border-amber-200"
                  }`}>{factura.estado === "cobrada" ? "Cobrada ✓" : "Pendiente cobro"}</span>
                </div>
                <div className="text-xs text-zinc-500">Emitida el {fmtFecha(factura.fecha_emision)} · {fmtEUR(factura.totales?.total || 0)}</div>
              </div>
              <a
                href={`${API}/portal-pres/${token}/factura.pdf`}
                target="_blank"
                rel="noreferrer"
                data-testid="btn-descargar-factura"
                className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white font-semibold rounded-lg text-sm"
              >
                <Download className="w-4 h-4" /> Descargar factura
              </a>
            </div>
          </div>
        )}

        {/* CTA principal — acciones del cliente */}
        {!aceptado && !rechazado && (
          <div className="bg-white rounded-2xl shadow-sm border border-zinc-200 p-5 mb-6">
            <div className="text-sm text-zinc-700 mb-3 font-semibold">¿Qué te parece?</div>
            <div className="flex flex-wrap gap-2">
              <Button onClick={() => setOpenDecision("aceptado")}
                data-testid="btn-aceptar"
                className="bg-emerald-500 hover:bg-emerald-600 text-white font-semibold flex-1 min-w-[150px]">
                <CheckCircle2 className="w-4 h-4 mr-2" /> Aceptar presupuesto
              </Button>
              <Button onClick={() => setOpenDecision("pedir_cambios")}
                data-testid="btn-cambios"
                variant="outline"
                className="border-amber-400 text-amber-700 hover:bg-amber-50 flex-1 min-w-[150px]">
                <Edit3 className="w-4 h-4 mr-2" /> Pedir cambios
              </Button>
              <Button onClick={() => setOpenDecision("rechazado")}
                data-testid="btn-rechazar"
                variant="ghost"
                className="text-zinc-500 hover:text-red-600 hover:bg-red-50 flex-1 min-w-[120px]">
                <XCircle className="w-4 h-4 mr-2" /> Rechazar
              </Button>
            </div>
            {decisionActual && (
              <div className="mt-3 text-xs text-zinc-500 italic">
                Última respuesta: <b>{decisionActual.replace("_", " ")}</b> el {fmtFecha(data.cliente_decision_at)}.
                {data.cliente_comentario && <div className="mt-1">"{data.cliente_comentario}"</div>}
              </div>
            )}
          </div>
        )}

        {/* PDF & descarga */}
        <div className="bg-white rounded-2xl shadow-sm border border-zinc-200 p-5 mb-6 flex items-center gap-3 flex-wrap">
          <FileText className="w-6 h-6 text-amber-500" />
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-zinc-900">Presupuesto en PDF</div>
            <div className="text-xs text-zinc-500">Descárgalo, imprímelo o guárdalo para tus registros.</div>
          </div>
          <a href={`${API}/portal-pres/${token}/pdf`} target="_blank" rel="noreferrer"
            data-testid="btn-descargar-pdf">
            <Button className="bg-zinc-900 hover:bg-zinc-800 text-white">
              <Download className="w-4 h-4 mr-2" /> Descargar PDF
            </Button>
          </a>
        </div>

        {/* Items */}
        <div className="bg-white rounded-2xl shadow-sm border border-zinc-200 p-5 mb-6">
          <div className="text-[11px] uppercase tracking-wider text-zinc-500 font-semibold mb-3 flex items-center justify-between">
            <span>Detalle</span>
            {(data.items || []).some((it) => it.opcional || it.grupo) && data.cliente_decision !== "aceptado" && (
              <span className="text-amber-700 normal-case tracking-normal text-xs font-normal">
                💡 Elige las opciones que quieras incluir
              </span>
            )}
          </div>
          {(() => {
            // Agrupar items: obligatorios, opcionales sueltos, grupos
            const items = data.items || [];
            const obligatorios = items.filter((it) => !it.opcional && !it.grupo);
            const opcionales = items.filter((it) => it.opcional && !it.grupo);
            const grupos = {};
            items.filter((it) => it.grupo).forEach((it) => {
              if (!grupos[it.grupo]) grupos[it.grupo] = [];
              grupos[it.grupo].push(it);
            });
            const editable = data.cliente_decision !== "aceptado" && data.estado !== "aceptado";

            const renderItem = (it, opts = {}) => {
              const subt = calcItem(it);
              const m2 = superficieM2(it);
              const incluido = (it.grupo || it.opcional) ? seleccion[it.id] : true;
              return (
                <div key={it.id}
                  data-testid={`item-${it.id}`}
                  className={`flex gap-3 pb-3 border-b border-zinc-100 last:border-0 transition-opacity ${
                    !incluido ? "opacity-40" : ""
                  }`}>
                  {/* Checkbox / Radio */}
                  {(it.opcional || it.grupo) && editable && (
                    <div className="pt-1 flex-shrink-0">
                      {it.grupo ? (
                        <input type="radio" name={`grupo-${it.grupo}`} checked={!!seleccion[it.id]}
                          onChange={() => toggleItem(it)}
                          data-testid={`radio-${it.id}`}
                          className="w-5 h-5 accent-amber-500 cursor-pointer" />
                      ) : (
                        <input type="checkbox" checked={!!seleccion[it.id]}
                          onChange={() => toggleItem(it)}
                          data-testid={`check-${it.id}`}
                          className="w-5 h-5 accent-amber-500 cursor-pointer rounded" />
                      )}
                    </div>
                  )}
                  {it.imagen_url && (
                    <a href={it.imagen_url} target="_blank" rel="noreferrer" className="flex-shrink-0">
                      <img src={it.imagen_url} alt="" className="w-20 h-20 object-cover rounded-lg border border-zinc-200" />
                    </a>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      {it.opcional && !it.grupo && (
                        <span className="text-[10px] uppercase tracking-wider bg-violet-100 text-violet-700 px-1.5 py-0.5 rounded font-semibold">Opcional</span>
                      )}
                      {it.grupo && (
                        <span className="text-[10px] uppercase tracking-wider bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-semibold">Alternativa</span>
                      )}
                      <div className="font-medium text-zinc-900">{it.descripcion}</div>
                    </div>
                    <div className="text-xs text-zinc-500 mt-0.5">
                      {it.base_cm && it.altura_cm
                        ? <>{it.base_cm}×{it.altura_cm} cm{m2 && it.unidad === "m2" ? <> · <b>{m2.toFixed(2)} m²</b></> : null}</>
                        : `${it.cantidad} ${it.unidad}`}
                      {it.cantidad !== 1 && it.unidad === "m2" && m2 ? ` × ${it.cantidad} ud` : ""}
                      {it.descuento_pct ? <span className="ml-2 text-orange-600 font-semibold">(-{it.descuento_pct}%)</span> : null}
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="text-amber-600 font-bold text-lg">{fmtEUR(subt)}</div>
                    <div className="text-[11px] text-zinc-500">{fmtEUR(it.precio_unitario)}/{it.unidad}</div>
                  </div>
                </div>
              );
            };

            return (
              <div className="space-y-3">
                {obligatorios.map((it) => renderItem(it))}

                {Object.entries(grupos).map(([nombreGrupo, items]) => (
                  <div key={nombreGrupo} className="bg-amber-50 border border-amber-200 rounded-lg p-3 my-3">
                    <div className="text-xs uppercase tracking-wider text-amber-800 font-semibold mb-2 flex items-center gap-1.5">
                      ⚙️ Alternativas · {nombreGrupo}
                      {editable && <span className="text-amber-600 normal-case tracking-normal">— elige una</span>}
                    </div>
                    <div className="space-y-3">
                      {items.map((it) => renderItem(it, { grupoName: nombreGrupo }))}
                    </div>
                  </div>
                ))}

                {opcionales.length > 0 && (
                  <div className="bg-violet-50 border border-violet-200 rounded-lg p-3 my-3">
                    <div className="text-xs uppercase tracking-wider text-violet-800 font-semibold mb-2 flex items-center gap-1.5">
                      ✨ Conceptos opcionales
                      {editable && <span className="text-violet-600 normal-case tracking-normal">— marca los que quieras incluir</span>}
                    </div>
                    <div className="space-y-3">
                      {opcionales.map((it) => renderItem(it))}
                    </div>
                  </div>
                )}
              </div>
            );
          })()}
          {/* Totales (recalculados con selección) */}
          <div className="flex justify-end mt-4 pt-4 border-t border-zinc-200">
            <div className="w-72 space-y-1.5 text-sm">
              <div className="flex justify-between text-zinc-600">
                <span>Subtotal seleccionado</span><span className="font-semibold">{fmtEUR(totalSeleccion.subtotal)}</span>
              </div>
              <div className="flex justify-between text-zinc-500 text-xs">
                <span>IVA ({data.iva_pct}%)</span><span>{fmtEUR(totalSeleccion.iva)}</span>
              </div>
              <div className="flex justify-between border-t border-amber-500/40 pt-2 text-lg">
                <span className="text-zinc-900 font-bold">TOTAL</span>
                <span className="text-amber-600 font-bold" data-testid="total-seleccion">{fmtEUR(totalSeleccion.total)}</span>
              </div>
              {totalSeleccion.total !== totales.total && (
                <div className="text-[10px] text-zinc-500 italic text-right">
                  Total original (todos los items): {fmtEUR(totales.total)}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Condiciones + Notas */}
        {(data.condiciones_pago || data.notas_cliente) && (
          <div className="grid md:grid-cols-2 gap-4 mb-6">
            {data.condiciones_pago && (
              <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5">
                <div className="text-[11px] uppercase tracking-wider text-amber-800 font-semibold mb-2">Condiciones</div>
                <div className="text-sm text-zinc-800 whitespace-pre-line">{data.condiciones_pago}</div>
              </div>
            )}
            {data.notas_cliente && (
              <div className="bg-zinc-100 border border-zinc-200 rounded-2xl p-5">
                <div className="text-[11px] uppercase tracking-wider text-zinc-600 font-semibold mb-2">Notas</div>
                <div className="text-sm text-zinc-800 whitespace-pre-line">{data.notas_cliente}</div>
              </div>
            )}
          </div>
        )}

        {/* Archivos para el cliente (subidos por DNF) */}
        {data.archivos_para_cliente && data.archivos_para_cliente.length > 0 && (
          <div className="bg-white rounded-2xl shadow-sm border border-zinc-200 p-5 mb-6">
            <div className="text-[11px] uppercase tracking-wider text-zinc-500 font-semibold mb-3 flex items-center gap-1.5">
              <Paperclip className="w-3.5 h-3.5" /> Archivos compartidos por DNF
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {data.archivos_para_cliente.map((a) => {
                const isImg = (a.tipo || "").startsWith("image/");
                return (
                  <a key={a.id} href={a.url} target="_blank" rel="noreferrer"
                    className="block bg-zinc-50 border border-zinc-200 rounded-lg overflow-hidden hover:border-amber-400 transition-colors"
                    data-testid={`arch-dnf-${a.id}`}>
                    {isImg ? (
                      <img src={a.url} alt={a.nombre} className="w-full h-28 object-cover" />
                    ) : (
                      <div className="w-full h-28 flex items-center justify-center text-zinc-400">
                        <FileText className="w-10 h-10" />
                      </div>
                    )}
                    <div className="p-2 text-xs text-zinc-700 truncate" title={a.nombre}>
                      {a.nombre}
                    </div>
                  </a>
                );
              })}
            </div>
          </div>
        )}

        {/* Subida del cliente */}
        <div className="bg-white rounded-2xl shadow-sm border border-zinc-200 p-5 mb-8">
          <div className="text-[11px] uppercase tracking-wider text-zinc-500 font-semibold mb-2 flex items-center gap-1.5">
            <Upload className="w-3.5 h-3.5" /> Enviar archivos a DNF
          </div>
          <p className="text-sm text-zinc-600 mb-3">
            Sube briefing, fotos del local, planos, logos en alta… Llegan directamente al equipo de DNF.
          </p>
          <Button onClick={() => fileRef.current?.click()} disabled={uploading}
            data-testid="btn-subir-archivo"
            className="bg-amber-500 hover:bg-amber-600 text-white">
            {uploading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Subiendo…</> : <><Upload className="w-4 h-4 mr-2" /> Subir archivo</>}
          </Button>
          <input ref={fileRef} type="file" className="hidden"
            onChange={(e) => { subirArchivo(e.target.files?.[0]); e.target.value = ""; }} />
          {data.archivos_cliente && data.archivos_cliente.length > 0 && (
            <div className="mt-4 space-y-1.5">
              <div className="text-[11px] uppercase tracking-wider text-zinc-500 font-semibold">Tus envíos</div>
              {data.archivos_cliente.map((a) => (
                <div key={a.id} className="flex items-center gap-2 text-sm" data-testid={`arch-cli-${a.id}`}>
                  <FileText className="w-3.5 h-3.5 text-zinc-400" />
                  <a href={a.url} target="_blank" rel="noreferrer"
                    className="text-zinc-700 hover:text-amber-600 truncate flex-1">{a.nombre}</a>
                  <span className="text-[11px] text-zinc-400">{fmtFecha(a.subido_at)}</span>
                  <button
                    onClick={async () => {
                      if (!window.confirm(`¿Eliminar "${a.nombre}"?`)) return;
                      try {
                        await axios.delete(`${API}/portal-pres/${token}/archivos/${a.id}`);
                        setData((prev) => ({ ...prev, archivos_cliente: prev.archivos_cliente.filter((x) => x.id !== a.id) }));
                        toast.success("Archivo eliminado");
                      } catch { toast.error("No se pudo eliminar"); }
                    }}
                    data-testid={`borrar-arch-cli-${a.id}`}
                    title="Eliminar archivo"
                    className="text-zinc-400 hover:text-red-500 transition-colors">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Chat con DNF */}
        <div className="bg-white rounded-2xl shadow-sm border border-zinc-200 p-5 mb-6">
          <div className="text-[11px] uppercase tracking-wider text-zinc-500 font-semibold mb-3 flex items-center gap-1.5">
            💬 Hablar con DNF
          </div>
          <p className="text-sm text-zinc-600 mb-3">
            ¿Tienes alguna duda o quieres comentarnos algo? Escríbenos por aquí, te respondemos lo antes posible.
          </p>
          <div className="bg-zinc-50 border border-zinc-200 rounded-lg p-3 max-h-72 overflow-y-auto mb-3 space-y-2"
            data-testid="chat-portal">
            {(data.mensajes || []).length === 0 && (
              <div className="text-xs text-zinc-500 italic text-center py-4">
                Aún no hay mensajes. Escribe tu primer comentario o duda abajo.
              </div>
            )}
            {(data.mensajes || []).map((m) => {
              const isCliente = m.autor_tipo === "cliente";
              return (
                <div key={m.id} className={`flex ${isCliente ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${
                    isCliente
                      ? "bg-amber-500 text-white rounded-br-sm"
                      : "bg-white border border-zinc-200 text-zinc-800 rounded-bl-sm shadow-sm"
                  }`}>
                    <div className={`text-[10px] mb-0.5 ${isCliente ? "text-amber-100" : "text-zinc-500"}`}>
                      <b>{m.autor_nombre}</b> · {new Date(m.created_at).toLocaleString("es-ES")}
                    </div>
                    <div className="whitespace-pre-wrap">{m.texto}</div>
                  </div>
                </div>
              );
            })}
            <div ref={chatBottomRef} />
          </div>
          <div className="flex gap-2">
            <Input value={chatMsg} onChange={(e) => setChatMsg(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); enviarChat(); } }}
              data-testid="chat-portal-input"
              placeholder="Escribe tu mensaje…"
              className="bg-white border-zinc-300 text-zinc-900 placeholder:text-zinc-400 flex-1" />
            <Button onClick={enviarChat} disabled={sendingChat || !chatMsg.trim()}
              data-testid="chat-portal-send"
              className="bg-amber-500 hover:bg-amber-600 text-white">
              {sendingChat ? <Loader2 className="w-4 h-4 animate-spin" /> : "Enviar"}
            </Button>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center text-xs text-zinc-500 py-6">
          {empresa.razon_social} · CIF {empresa.cif} · {empresa.telefono} · {empresa.email}
        </div>
      </div>

      {/* Dialog decisión */}
      <Dialog open={!!openDecision} onOpenChange={(o) => !o && setOpenDecision(null)}>
        <DialogContent className="bg-white" data-testid="dialog-decision">
          <DialogHeader>
            <DialogTitle className={
              openDecision === "aceptado" ? "text-emerald-600" :
              openDecision === "rechazado" ? "text-red-600" : "text-amber-600"
            }>
              {openDecision === "aceptado" && <>✅ Aceptar presupuesto</>}
              {openDecision === "pedir_cambios" && <>✏️ Pedir cambios</>}
              {openDecision === "rechazado" && <>❌ Rechazar presupuesto</>}
            </DialogTitle>
          </DialogHeader>
          <div className="py-2 space-y-3">
            {openDecision === "aceptado" && (
              <div className="text-sm text-zinc-700">
                Confirmas que aceptas el presupuesto <b>{data.numero}</b> por <b>{fmtEUR(totalSeleccion.total)}</b> (IVA incl.) con los conceptos seleccionados.
                {(data.items || []).some((it) => it.opcional || it.grupo) && (
                  <div className="mt-2 p-2 bg-zinc-50 rounded text-xs border border-zinc-200">
                    <b>Vas a aceptar:</b>
                    <ul className="mt-1 space-y-0.5">
                      {(data.items || []).filter((it) => seleccion[it.id] || (!it.opcional && !it.grupo)).map((it) => (
                        <li key={it.id}>✓ {it.descripcion}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
            {openDecision === "pedir_cambios" && (
              <div className="text-sm text-zinc-700">
                Cuéntanos qué quieres cambiar y te enviaremos una nueva versión.
              </div>
            )}
            {openDecision === "rechazado" && (
              <div className="text-sm text-zinc-700">
                ¿Hay algo que podamos mejorar para futuros presupuestos?
              </div>
            )}
            <div>
              <Label className="text-xs text-zinc-500">
                {openDecision === "aceptado" ? "Comentarios (opcional)" :
                 openDecision === "pedir_cambios" ? "¿Qué cambios necesitas?" :
                 "Motivo (opcional)"}
              </Label>
              <Textarea value={comentario} onChange={(e) => setComentario(e.target.value)}
                data-testid="textarea-comentario"
                placeholder={
                  openDecision === "pedir_cambios"
                    ? "Ej: el render queda muy oscuro, prefiero el logo más grande, cambiar dimensiones a 200×150…"
                    : "Escribe aquí si quieres añadir algo"
                }
                className="bg-white border-zinc-300 text-zinc-900 placeholder:text-zinc-400 min-h-[100px] mt-1" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpenDecision(null)} data-testid="btn-cancel-decision">Cancelar</Button>
            <Button onClick={enviarDecision} disabled={busy || (openDecision === "pedir_cambios" && !comentario.trim())}
              data-testid="btn-confirm-decision"
              className={
                openDecision === "aceptado" ? "bg-emerald-500 hover:bg-emerald-600 text-white" :
                openDecision === "rechazado" ? "bg-red-500 hover:bg-red-600 text-white" :
                "bg-amber-500 hover:bg-amber-600 text-white"
              }>
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Confirmar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// ============ KANBAN: Progreso del pedido ============
function KanbanProgreso({ progreso }) {
  const etapas = progreso.etapas || [];
  const etapaActualIdx = etapas.findIndex((e) => e.key === progreso.etapa_global);
  const lineasPorEtapa = {};
  etapas.forEach((e) => { lineasPorEtapa[e.key] = []; });
  (progreso.lineas || []).forEach((ln) => {
    if (lineasPorEtapa[ln.etapa]) lineasPorEtapa[ln.etapa].push(ln);
  });

  const COMP_LABEL = {
    produccion: "Producción", montaje: "Montaje", diseno: "Diseño", externo: "Proveedor externo",
  };
  const ESTADO_COMP_LABEL = {
    pendiente: "Pendiente", en_produccion: "En producción", producido: "Producido",
    recibido_almacen: "En almacén", en_curso: "En curso", finalizado: "Finalizado",
    aprobado: "Aprobado", entregado: "Entregado", instalado: "Instalado",
  };

  return (
    <div className="bg-gradient-to-br from-emerald-50 to-white rounded-2xl shadow-sm border-2 border-emerald-300 p-5 mb-6" data-testid="kanban-progreso">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-emerald-700 font-semibold flex items-center gap-1.5">
            🚀 Estado de tu pedido
          </div>
          <div className="text-xl font-bold text-zinc-900 mt-0.5">
            {progreso.peticion_titulo || progreso.peticion_numero}
          </div>
        </div>
        <div className="text-xs text-zinc-500">
          Se actualiza automáticamente · Última actualización:{" "}
          <b>{progreso.ultima_actualizacion ? new Date(progreso.ultima_actualizacion).toLocaleString("es-ES") : "—"}</b>
        </div>
      </div>

      {/* Barra de progreso con las 5 etapas */}
      <div className="relative mb-6">
        <div className="absolute top-5 left-5 right-5 h-1 bg-zinc-200 rounded-full" />
        <div className="absolute top-5 left-5 h-1 bg-emerald-500 rounded-full transition-all duration-700"
          style={{ width: `calc(${(etapaActualIdx / Math.max(1, etapas.length - 1)) * 100}% - ${etapaActualIdx === etapas.length - 1 ? 0 : 20}px)` }} />
        <div className="relative grid" style={{ gridTemplateColumns: `repeat(${etapas.length}, 1fr)` }}>
          {etapas.map((e, idx) => {
            const completed = idx < etapaActualIdx;
            const active = idx === etapaActualIdx;
            const count = (lineasPorEtapa[e.key] || []).length;
            return (
              <div key={e.key} className="flex flex-col items-center" data-testid={`etapa-${e.key}`}>
                <div className={`w-11 h-11 rounded-full flex items-center justify-center text-xl border-2 transition-all ${
                  active ? "bg-emerald-500 border-emerald-600 text-white scale-110 shadow-lg" :
                  completed ? "bg-emerald-100 border-emerald-400 text-emerald-700" :
                  "bg-white border-zinc-300 text-zinc-400"
                }`}>
                  {completed ? "✓" : e.emoji}
                </div>
                <div className={`text-[11px] mt-1.5 text-center font-semibold ${
                  active ? "text-emerald-700" : completed ? "text-zinc-700" : "text-zinc-400"
                }`}>
                  {e.label}
                </div>
                {count > 0 && (
                  <div className={`text-[10px] mt-0.5 px-1.5 py-0.5 rounded-full font-bold ${
                    active ? "bg-emerald-500 text-white" : "bg-zinc-200 text-zinc-600"
                  }`}>{count}</div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Detalle por línea */}
      <div className="space-y-2">
        <div className="text-[11px] uppercase tracking-wider text-zinc-500 font-semibold mb-2">
          Detalle por concepto ({progreso.lineas?.length || 0})
        </div>
        {(progreso.lineas || []).map((ln) => {
          const etapaCfg = etapas.find((e) => e.key === ln.etapa) || etapas[0];
          return (
            <div key={ln.id}
              data-testid={`kanban-linea-${ln.id}`}
              className="bg-white border border-zinc-200 rounded-lg p-3 flex items-center gap-3">
              {ln.imagen_url && (
                <img src={ln.imagen_url} alt="" className="w-14 h-14 object-cover rounded border border-zinc-200 flex-shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <div className="font-medium text-zinc-900 text-sm">{ln.descripcion}</div>
                {ln.componentes && ln.componentes.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1">
                    {ln.componentes.map((c, i) => (
                      <span key={i} className="text-[10px] bg-zinc-100 text-zinc-600 px-1.5 py-0.5 rounded">
                        {COMP_LABEL[c.tipo] || c.tipo}: <b>{ESTADO_COMP_LABEL[c.estado] || c.estado}</b>
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <span className={`text-xs font-bold px-2 py-1 rounded ${
                ln.etapa === "entregado" ? "bg-emerald-100 text-emerald-700" :
                ln.etapa === "listo" ? "bg-blue-100 text-blue-700" :
                ln.etapa === "produccion" ? "bg-amber-100 text-amber-700" :
                ln.etapa === "diseno" ? "bg-violet-100 text-violet-700" :
                "bg-zinc-100 text-zinc-600"
              }`}>
                {etapaCfg.emoji} {etapaCfg.label}
              </span>
            </div>
          );
        })}
      </div>

      {/* Eventos recientes */}
      {progreso.eventos && progreso.eventos.length > 0 && (
        <div className="mt-4 pt-4 border-t border-emerald-200">
          <div className="text-[11px] uppercase tracking-wider text-zinc-500 font-semibold mb-2">
            Últimas actualizaciones
          </div>
          <div className="space-y-1.5">
            {progreso.eventos.slice().reverse().map((ev, i) => (
              <div key={i} className="flex items-start gap-2 text-xs">
                <span className="text-emerald-500 mt-0.5">●</span>
                <div className="flex-1">
                  <div className="text-zinc-700">{ev.descripcion}</div>
                  <div className="text-zinc-400 text-[10px]">
                    {ev.autor_nombre} · {new Date(ev.created_at).toLocaleString("es-ES")}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


// ============ Componente: PagosCliente ============
function PagosCliente({ token, totales, importePagado, estadoPago, pagos, cobroHabilitado, iniciar, busy }) {
  const total = totales?.total || 0;
  const pagado = Number(importePagado) || 0;
  const pendiente = Math.max(total - pagado, 0);
  const pctPagado = total > 0 ? Math.min(100, (pagado / total) * 100) : 0;
  const completo = estadoPago === "pagado" || pendiente < 0.01;
  const hayCobro = !!(cobroHabilitado && Number(cobroHabilitado.importe) > 0);

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-zinc-200 p-5 mb-6" data-testid="portal-pagos">
      <div className="flex items-center gap-2 mb-3">
        <CreditCard className="w-5 h-5 text-amber-600" />
        <h3 className="text-base font-semibold text-zinc-900">Pago seguro</h3>
        {completo && (
          <span className="ml-auto inline-flex items-center gap-1 text-xs font-semibold text-emerald-600 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-0.5">
            <BadgeCheck className="w-3.5 h-3.5" /> Pagado completo
          </span>
        )}
      </div>

      {/* Big number summary: Prepagado / Restante */}
      <div className="grid grid-cols-3 gap-2 mb-4">
        <div className="bg-zinc-50 rounded-xl p-3 text-center border border-zinc-200">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">Importe total</div>
          <div className="text-lg font-bold text-zinc-900 mt-0.5">{fmtEUR(total)}</div>
        </div>
        <div className="bg-emerald-50 rounded-xl p-3 text-center border border-emerald-200">
          <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-semibold">Prepagado</div>
          <div className="text-lg font-bold text-emerald-700 mt-0.5">{fmtEUR(pagado)}</div>
        </div>
        <div className={`rounded-xl p-3 text-center border ${completo ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-200"}`}>
          <div className={`text-[10px] uppercase tracking-wider font-semibold ${completo ? "text-emerald-700" : "text-amber-700"}`}>Restante</div>
          <div className={`text-lg font-bold mt-0.5 ${completo ? "text-emerald-700" : "text-amber-700"}`}>{fmtEUR(pendiente)}</div>
        </div>
      </div>

      {/* Barra de progreso */}
      <div className="mb-4">
        <div className="h-2 bg-zinc-100 rounded-full overflow-hidden">
          <div
            className={`h-full transition-all ${completo ? "bg-emerald-500" : "bg-amber-500"}`}
            style={{ width: `${pctPagado}%` }}
            data-testid="portal-pagos-progress"
          />
        </div>
      </div>

      {/* Botón de pago — sólo si DNF ha habilitado un cobro explícito */}
      {!completo && hayCobro && (
        <div className="mb-4">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-3 text-center">
            <div className="text-[10px] uppercase tracking-wider text-amber-700 font-semibold mb-1">
              {cobroHabilitado.concepto || "Cobro pendiente"}
            </div>
            <div className="text-2xl font-bold text-amber-900">{fmtEUR(cobroHabilitado.importe)}</div>
          </div>
          <Button
            data-testid="btn-pagar-habilitado"
            onClick={() => iniciar()}
            disabled={busy}
            className="w-full bg-amber-500 hover:bg-amber-600 text-white font-semibold h-11"
          >
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CreditCard className="w-4 h-4 mr-2" />}
            Pagar {fmtEUR(cobroHabilitado.importe)} con tarjeta
          </Button>
        </div>
      )}

      {/* Mensaje de espera — aceptado pero sin cobro habilitado todavía */}
      {!completo && !hayCobro && (
        <div className="mb-4 bg-zinc-50 border border-zinc-200 rounded-lg p-4 text-center" data-testid="portal-pago-en-espera">
          <div className="text-amber-600 text-2xl mb-1">📩</div>
          <div className="text-sm font-semibold text-zinc-800 mb-0.5">Tu pedido está aceptado</div>
          <div className="text-xs text-zinc-600">
            RotulaciónDNF te avisará cuando el cobro esté listo. Recibirás un aviso por WhatsApp o email con el importe a abonar.
          </div>
        </div>
      )}

      {/* Histórico */}
      {pagos.length > 0 && (
        <div className="pt-3 border-t border-zinc-200">
          <div className="text-[11px] uppercase tracking-wider text-zinc-500 font-semibold mb-2">
            Histórico de pagos
          </div>
          <div className="space-y-1.5">
            {pagos.map((p) => (
              <div key={p.id} className="flex items-center gap-2 text-xs" data-testid={`pago-row-${p.id}`}>
                <span className={`w-2 h-2 rounded-full ${
                  p.estado === "pagado" ? "bg-emerald-500" :
                  p.estado === "fallido" ? "bg-red-500" :
                  "bg-amber-500"
                }`} />
                <span className="text-zinc-700 flex-1">
                  {p.concepto || (p.tipo === "anticipo_50" ? "Anticipo 50%" : p.tipo === "resto_final" ? "Resto final" : p.tipo === "total" ? "Pago total" : "Pago")}
                  {" · "}
                  <span className="text-zinc-500">{new Date(p.created_at).toLocaleString("es-ES")}</span>
                </span>
                <span className="font-semibold text-zinc-900">{fmtEUR(p.importe)}</span>
                <span className={`text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded ${
                  p.estado === "pagado" ? "bg-emerald-50 text-emerald-700" :
                  p.estado === "fallido" ? "bg-red-50 text-red-700" :
                  "bg-amber-50 text-amber-700"
                }`}>{p.estado}</span>
                {p.estado === "pagado" && (
                  <a
                    href={`${API}/portal-pres/${token}/pagos/${p.id}/recibo.pdf`}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`btn-recibo-${p.id}`}
                    className="text-amber-600 hover:text-amber-700 underline text-[10px] font-semibold"
                  >
                    Recibo PDF
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-3 text-[10px] text-zinc-400 flex items-center gap-1">
        🔒 Pago seguro con tarjeta · TPV Virtual La Caixa (Redsys)
      </div>
    </div>
  );
}
